import { useQuery, skipToken } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { IApplicationData } from '@apply/api/src/api/ApplyApi';
import { applicationQueryKey } from './applicationQueries';

/**
 * Reads a derived value from the React Query application cache without making an API call.
 *
 * This is the base utility for all `useXxxCache` hooks. The component re-renders
 * only when the selected value changes (referential equality).
 *
 * @param selector - derives the value from IApplicationData
 * @returns the selected value, or undefined if the cache is empty
 *
 * @example
 * export function usePropertyGuidsCache(): string[] {
 *     return useApplicationCache(d =>
 *         d.information?.properties?.items?.map(p => p.guid) ?? []
 *     ) ?? [];
 * }
 */
export function useApplicationCache<T>(selector: (d: IApplicationData) => T): T | undefined {
    const { entryGuid } = useParams<{ entryGuid?: string }>();
    return useQuery<IApplicationData, Error, T>({
        queryKey: applicationQueryKey(entryGuid ?? ''),
        queryFn: skipToken,
        select: selector,
    }).data;
}
